import { DEV, createEffect, createMemo, onCleanup } from "solid-js"
import { DoubleSide, Group, Mesh, MeshLambertMaterial, type BufferGeometry } from "three"
import { useNodeNeighborBaseFloorElevationReactiveMap, type NeighborElevationCollection } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import type { Nullable } from "../../../../../typescript"
import removeItem from "../../../../array/remove_item"
import type { Engine } from "../../../../core/engine/Engine.type"
import type { TileGraphNode } from "../../../../core/engine/TileGraphNode/TileGraphNode.type"
import { ELEVATION_SIZE } from "../../../../core/entity/EntityElevation/elevation_consts"
import isEntityLayerIdTerrain1 from "../../../../core/entity/EntityLayerId/isEntityLayerIdTerrain1"
import { EntityTypeId } from "../../../../core/entity/EntityTypeId.enum"
import type { QuadrantBound } from "../../../../Quadtree/createEntityQuadtreeEffect"
import { VERTICES_PER_TILE } from "../../../consts"
import runWithObject3DOwner from "../../../fn/runWithObject3DOwner"
import InwardPointedPlaneGeometry from "../../mesh/InwardPointedPlaneGeometry.class"
import createTerrainMesh1DetailPropsEffect from "../../TerrainMesh1V2/createTerrainMesh1DetailPropsEffect"
import getTerrainMesh1Z from "../../TerrainMesh1V2/getTerrainMesh1Z"
import addTileFloorTextureEffectV2 from "./Texture/addTileFloorTextureEffectV2"

export const FLOOR_SEGMENTS = 64 * 0.25
// export const FLOOR_SEGMENTS_LOW = FLOOR_SEGMENTS
// export const FLOOR_SEGMENTS_LOW = (FLOOR_SEGMENTS / 2) | 0 // visible gaps!
// export const FLOOR_SEGMENTS_LOW = (FLOOR_SEGMENTS / 4) | 0 // visible gaps!
const size = (VERTICES_PER_TILE * 1)
// const halfSize = size / 2

// const outColor = new Color()

// const groupRng = createNewRNGConfig()
// const groupNoise2D = createNoise2D(() => next(groupRng))

// const wzRatio = 1 / 16

// const sandDuneTop = new Color(0xF7DC8A)
// if (DEV) {
//   freeze(sandDuneTop)
// }
// const wetSand = new Color(0xD4AC0D)
// if (DEV) {
//   freeze(wetSand)
// }

export interface TileFloorGroupGeometryUserData {
  out: Nullable<BufferGeometry>
  node: TileGraphNode
  bec: NeighborElevationCollection
  etype_id: Nullable<EntityTypeId>
}

// V1: use getTerrainMesh1Z.ts on the CPU
// V2: use a GLSL version of getTerrainMesh1Z on the GPU
const enum TerrainMesh1V3TileFloorGroupHeightVersion {
  None,
  FlatElevation,
  GetTerrainMesh1Z,
  Pyramid,
  Mound,
}
const heightVersion: TerrainMesh1V3TileFloorGroupHeightVersion = TerrainMesh1V3TileFloorGroupHeightVersion.GetTerrainMesh1Z

export default function createTerrainMesh1V3TileFloorGroup(
  engine: Engine,
  subNodeList: Array<TileGraphNode>,
  quadrant: QuadrantBound,
  geometryLowList: Array<BufferGeometry>,
  dirtyGeometryLowList: () => void,
  materialList: Array<MeshLambertMaterial>,
  dirtyMaterialList: () => void,
): Group {
  // console.log("createTerrainMesh1V3TileFloorGroup")
  // console.time("createTerrainMesh1V3TileFloorGroup")
  const group = new Group()
  group.name = `TerrainMesh1X${quadrant.x}Y${quadrant.y}`
  group.userData.quadrant = quadrant
  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)

  subNodeList.forEach((node) => {
    const nodeIndex = node.index
    const material = new MeshLambertMaterial({
      // vertexColors: true,
    })
    if (DEV) {
      material.side = DoubleSide
      // material.wireframe = true
    }

    const getEntityTypeId = node.ents.l1.etype_id

    const geometry = new InwardPointedPlaneGeometry(size, size, FLOOR_SEGMENTS, FLOOR_SEGMENTS)
    const geometryLow = new InwardPointedPlaneGeometry(size, size, FLOOR_SEGMENTS, FLOOR_SEGMENTS)
    geometryLowList.push(geometryLow)
    onCleanup(() => {
      removeItem(geometryLowList, geometryLow)
    })
    const geometryLowUserData: TileFloorGroupGeometryUserData = geometryLow.userData as TileFloorGroupGeometryUserData
    geometryLowUserData.node = node

    // const positionAttr = geometry.attributes.position
    // const colors = new Float32Array(positionAttr.count * 3)
    // const positionAttrLow = geometryLow.attributes.position
    // const colorsLow = new Float32Array(positionAttrLow.count * 3)
    // console.log('colorsLow', colorsLow.length)

    // geometry.setAttribute("color", new BufferAttribute(colors, 3))
    // geometryLow.setAttribute("color", new BufferAttribute(colorsLow, 3))

    const mesh = new Mesh(geometry, material)
    mesh.userData.node = node
    mesh.name = `X${node.x}Y${node.y}L1`
    mesh.receiveShadow = true
    mesh.castShadow = false

    mesh.position.set((node.x - quadrant.x) * VERTICES_PER_TILE, (node.y - quadrant.y) * -VERTICES_PER_TILE, 0)
    group.add(mesh)

    // const eightNeighbors = getEightTilePositionNeighborsList(node)

    // const getEightNeighborSameTypeList: Accessor<Array<Nullable<Entity>>> = createMemo(() => {
    //   const etype_id = getEntityTypeId()
    //   if (etype_id) {
    //     return eightNeighbors.map((neighbor) => {
    //       return neighbor?.ents().find(ent2 => ent2.etype_id === etype_id)
    //     })
    //   }
    //   return immutableEmptyArray as unknown as Array<Nullable<Entity>>
    // }, immutableEmptyArray as unknown as Array<Nullable<Entity>>, {
    //   equals: isIdAndEtypeIdListSameNilable
    // })

    const tileGeometryList = [geometry, geometryLow]

    // Reactive effect for color change
    runWithObject3DOwner(mesh, () => {
      createEffect(() => {
        const etype_id = getEntityTypeId()
        // console.log(`Node${node.index}`, etype_id ? entityDisplayName({ etype_id }) : "")

        const baseElevationsCollection = neighborBaseElevationReactiveMap.get(nodeIndex)
        // if (samePosition(node, coord(19, 11))) {
        //   // console.log(`Index[${nodeIndex}]`, { x: node.x, y: node.y })
        //   console.log(`baseElevationsCollection[${nodeIndex}].base`, baseElevationsCollection?.base)
        // }
        // if (samePosition(node, coord(20, 11))) {
        //   console.log(`Index[${nodeIndex}]`, { x: node.x, y: node.y })
        //   console.log(`baseElevationsCollection[${nodeIndex}].list`, baseElevationsCollection?.list)
        // }

        if (!baseElevationsCollection) {
          return
        }

        if (heightVersion === TerrainMesh1V3TileFloorGroupHeightVersion.FlatElevation) {
          const flatRenderHeight = baseElevationsCollection.base * ELEVATION_SIZE
          for (let i = 0; i < tileGeometryList.length; i++) {
            const geometry = tileGeometryList[i]
            const positionAttr = geometry.attributes.position
            const positionAttrLen = positionAttr.count
            for (let i = 0; i < positionAttrLen; i++) {
              positionAttr.setZ(i, flatRenderHeight)
            }
            geometry.computeVertexNormals()
            geometry.attributes.position.needsUpdate = true
          }
        } else if (heightVersion === TerrainMesh1V3TileFloorGroupHeightVersion.GetTerrainMesh1Z) {
          for (let i = 0; i < tileGeometryList.length; i++) {
            const geometry = tileGeometryList[i]
            const positionAttr = geometry.attributes.position
            const positionAttrLen = positionAttr.count
            for (let i = 0; i < positionAttrLen; i++) {
              const x = positionAttr.getX(i)
              const y = positionAttr.getY(i)
              const z = getTerrainMesh1Z(baseElevationsCollection, x, y)
              positionAttr.setZ(i, z)
            }
            geometry.computeVertexNormals()
            geometry.attributes.position.needsUpdate = true
          }
        } else if (heightVersion === TerrainMesh1V3TileFloorGroupHeightVersion.Pyramid) {
          const radius = VERTICES_PER_TILE / 2
          for (let i = 0; i < tileGeometryList.length; i++) {
            const geometry = tileGeometryList[i]
            const positionAttr = geometry.attributes.position
            const positionAttrLen = positionAttr.count
            for (let i = 0; i < positionAttrLen; i++) {
              const x = positionAttr.getX(i)
              const y = positionAttr.getY(i)
              const edgeDistX = Math.abs(x) / radius
              const edgeDistY = Math.abs(y) / radius
              const z = Math.max(0, 1 - Math.max(edgeDistX, edgeDistY)) * ELEVATION_SIZE * VERTICES_PER_TILE
              positionAttr.setZ(i, z)
            }
            geometry.computeVertexNormals()
            geometry.attributes.position.needsUpdate = true
          }
        } else if (heightVersion === TerrainMesh1V3TileFloorGroupHeightVersion.Mound) {
          const radius = VERTICES_PER_TILE / 2
          for (let i = 0; i < tileGeometryList.length; i++) {
            const geometry = tileGeometryList[i]
            const positionAttr = geometry.attributes.position
            const positionAttrLen = positionAttr.count
            for (let i = 0; i < positionAttrLen; i++) {
              const x = positionAttr.getX(i)
              const y = positionAttr.getY(i)
              const distanceFromCenter = Math.sqrt(x * x + y * y)
              const normalizedDistance = Math.min(distanceFromCenter / radius, 1)
              // Use cosine for smooth rounded top instead of linear falloff
              const smoothFalloff = Math.cos(normalizedDistance * Math.PI * 0.5)
              const z = Math.max(0, smoothFalloff) * ELEVATION_SIZE * VERTICES_PER_TILE
              positionAttr.setZ(i, z)
            }
            geometry.computeVertexNormals()
            geometry.attributes.position.needsUpdate = true
          }
        }

        geometryLowUserData.node = node
        geometryLowUserData.bec = baseElevationsCollection
        geometryLowUserData.etype_id = etype_id

        const { out } = geometryLowUserData
        if (out) {
          out.dispose()
          geometryLowUserData.out = null
        }
        dirtyGeometryLowList()
        // addTileFloorTextureEffectV1(node, material, neighborBaseElevationReactiveMap)
        addTileFloorTextureEffectV2(engine, node, material, neighborBaseElevationReactiveMap, materialList, dirtyMaterialList)
      })
    })

    {
      const getEnt = createMemo(() => node.ents().find(isEntityLayerIdTerrain1))
      // eslint-disable-next-line solid/reactivity
      createTerrainMesh1DetailPropsEffect(neighborBaseElevationReactiveMap, nodeIndex, mesh, getEnt)
    }
  })

  return group
}
